import { DataId } from './interface';

/** The rows of a list the company page steps through, by absolute index. */
export interface IndexedRows {
  readonly key: string;
  readonly totalRecords: number;
  readonly isComplete: boolean;
  indexOf(key: unknown): number;
  rowAt(index: number): Promise<object | null>;
}

/** What the company page shows: previous, next and "12 / 17,416". */
export interface PaginationInfo {
  hasNext: boolean;
  hasPrev: boolean;
  /** 1-based position in the whole list; null when the company is not found. */
  position: number | null;
  total: number | null;
}

export const NO_PAGINATION: PaginationInfo = {
  hasNext: false,
  hasPrev: false,
  position: null,
  total: null,
};

/** A company of the list and its index in the whole list. */
export interface IndexedRow {
  id: string;
  index: number;
}

/**
 * Previous and next company of a list by the absolute index of the shown one,
 * which the company URL carries (`row`). The rows of the index come from the
 * source, which loads the chunk of a row that is not loaded yet.
 */
export class RowNavigator {
  constructor(private rows: IndexedRows | null) {}

  private idAt(row: object | null) {
    return row && this.rows
      ? ((row as Record<string, unknown>)[this.rows.key] as DataId)
      : null;
  }

  /**
   * The index of the company: the one of the URL when the row there is still
   * this company, else where it is among the loaded rows; null if neither.
   */
  async locate(id: DataId, hint: number | null): Promise<number | null> {
    const rows = this.rows;
    if (!rows) {
      return null;
    }
    if (hint !== null && Number.isInteger(hint) && hint >= 0) {
      if (this.idAt(await rows.rowAt(hint)) === id) {
        return hint;
      }
    } else {
      // Loads the first chunk.
      await rows.rowAt(0);
    }
    const index = rows.indexOf(id);
    return index >= 0 ? index : null;
  }

  info(index: number | null): PaginationInfo {
    const rows = this.rows;
    if (!rows || index === null) {
      return NO_PAGINATION;
    }
    const total = rows.totalRecords;
    return {
      hasNext: index + 1 < total || (!rows.isComplete && !total),
      hasPrev: index > 0,
      position: index + 1,
      total,
    };
  }

  /** The company `delta` rows away, loading its chunk when needed. */
  async step(index: number, delta: number): Promise<IndexedRow | null> {
    const target = index + delta;
    if (!this.rows || target < 0) {
      return null;
    }
    const id = this.idAt(await this.rows.rowAt(target));
    return id === null || id === undefined
      ? null
      : { id: String(id), index: target };
  }
}
